import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText, Building, Calendar, CheckCircle, Download, Trash2,
  ChevronLeft, ChevronRight, Loader, Eye, Clock, Play, Power,
  Edit2, Plus, X, RefreshCw, ChevronDown, ChevronUp, Award, BarChart2
} from 'lucide-react';
import {
  getConstructorTemplates, getDepartments, generateConstructorReport,
  getGeneratedReports, downloadGeneratedReport, deleteGeneratedReport,
  downloadBlob, getScheduledReports, createScheduledReport,
  updateScheduledReport, deleteScheduledReport, toggleScheduledReport,
  runScheduledReportNow,
  getConsolidatedReports, getConsolidatedReport, createConsolidatedReport,
  updateConsolidatedNarratives, refreshConsolidatedData,
  generateConsolidatedNarratives, generateConsolidatedDoc,
  downloadConsolidatedDoc, approveConsolidatedReport, deleteConsolidatedReport
} from '../services/api';

const STEP_KEYS = ['template', 'period', 'departments', 'preview', 'generation'];

const SECTION_KEYS = [
  'summary', 'kpi', 'reports_stats', 'documents_stats', 'plans_stats',
  'trends', 'problems', 'achievements', 'recommendations',
  'weekly_execution', 'comparison',
];

const PERIOD_TYPE_VALUES = ['weekly', 'monthly', 'quarterly', 'annual'];
const FORMAT_OPTION_VALUES = ['pdf', 'word'];
const SCHEDULE_TYPE_VALUES = ['weekly', 'monthly', 'quarterly'];

// --- Стили ---
const styles = {
  page: {
    padding: '24px',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '28px',
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1e3a5f',
    margin: 0,
  },
  // Индикаторы шагов
  stepsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0',
    marginBottom: '32px',
  },
  stepItem: (active, completed) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'default',
  }),
  stepCircle: (active, completed) => ({
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: '14px',
    color: active ? '#fff' : completed ? '#fff' : '#94a3b8',
    background: active ? '#1e3a5f' : completed ? '#3b82f6' : '#e2e8f0',
    transition: 'all 0.2s',
    flexShrink: 0,
  }),
  stepLabel: (active) => ({
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    color: active ? '#1e3a5f' : '#64748b',
    whiteSpace: 'nowrap',
  }),
  stepConnector: {
    width: '40px',
    height: '2px',
    background: '#e2e8f0',
    margin: '0 8px',
    flexShrink: 0,
  },
  // Карточка-контейнер
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    padding: '28px',
    marginBottom: '24px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
    marginTop: 0,
    marginBottom: '20px',
  },
  // Сетка шаблонов
  templatesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  templateCard: (selected) => ({
    border: selected ? '2px solid #1e3a5f' : '2px solid #e2e8f0',
    borderRadius: '10px',
    padding: '18px',
    cursor: 'pointer',
    background: selected ? '#f0f5ff' : '#fff',
    transition: 'all 0.15s',
    position: 'relative',
  }),
  templateName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '6px',
  },
  templateAddressee: {
    fontSize: '13px',
    color: '#3b82f6',
    fontWeight: 500,
    marginBottom: '4px',
  },
  templateDetail: {
    fontSize: '12px',
    color: '#94a3b8',
    marginBottom: '8px',
  },
  templateDesc: {
    fontSize: '13px',
    color: '#64748b',
    lineHeight: '1.4',
  },
  templateCheck: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    color: '#1e3a5f',
  },
  // Радио-группа
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px',
  },
  radioRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '20px',
  },
  radioOption: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 18px',
    borderRadius: '8px',
    border: selected ? '2px solid #1e3a5f' : '2px solid #e2e8f0',
    background: selected ? '#f0f5ff' : '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: selected ? 600 : 400,
    color: selected ? '#1e3a5f' : '#475569',
    transition: 'all 0.15s',
  }),
  // Даты
  dateRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dateLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
  },
  dateInput: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    color: '#1e293b',
    minWidth: '160px',
  },
  presetBtns: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
  presetBtn: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: '#f8fafc',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#475569',
    fontWeight: 500,
    transition: 'all 0.15s',
  },
  // Отделы
  departmentModeRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  deptList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '8px',
    maxHeight: '360px',
    overflowY: 'auto',
    padding: '4px',
  },
  deptItem: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: selected ? '1px solid #3b82f6' : '1px solid #e2e8f0',
    background: selected ? '#eff6ff' : '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#1e293b',
    transition: 'all 0.12s',
  }),
  checkbox: (checked) => ({
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    border: checked ? '2px solid #1e3a5f' : '2px solid #cbd5e1',
    background: checked ? '#1e3a5f' : '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.12s',
  }),
  checkMark: {
    color: '#fff',
    fontSize: '12px',
    fontWeight: 700,
  },
  // Предпросмотр
  previewSection: {
    marginBottom: '16px',
  },
  previewLabel: {
    fontSize: '13px',
    color: '#94a3b8',
    fontWeight: 500,
    marginBottom: '4px',
  },
  previewValue: {
    fontSize: '15px',
    color: '#1e293b',
    fontWeight: 500,
  },
  sectionsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  sectionChip: {
    padding: '5px 12px',
    borderRadius: '16px',
    background: '#eff6ff',
    color: '#1e3a5f',
    fontSize: '13px',
    fontWeight: 500,
    border: '1px solid #bfdbfe',
  },
  // Генерация
  genCenter: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    padding: '20px 0',
  },
  genBtn: (disabled) => ({
    padding: '14px 40px',
    borderRadius: '10px',
    border: 'none',
    background: disabled ? '#94a3b8' : '#1e3a5f',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    transition: 'all 0.2s',
  }),
  downloadBtn: {
    padding: '12px 32px',
    borderRadius: '10px',
    border: 'none',
    background: '#059669',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
  },
  successMsg: {
    color: '#059669',
    fontSize: '15px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  // Навигация
  navRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },
  navBtn: (disabled) => ({
    padding: '10px 22px',
    borderRadius: '8px',
    border: 'none',
    background: disabled ? '#e2e8f0' : '#1e3a5f',
    color: disabled ? '#94a3b8' : '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.15s',
  }),
  // История
  historySection: {
    marginTop: '40px',
  },
  historyTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#1e3a5f',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    background: '#f8fafc',
    color: '#64748b',
    fontWeight: 600,
    fontSize: '13px',
    borderBottom: '2px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9',
    color: '#1e293b',
    verticalAlign: 'middle',
  },
  actionBtn: (color) => ({
    padding: '6px 10px',
    borderRadius: '6px',
    border: 'none',
    background: 'transparent',
    color: color,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    fontWeight: 500,
    transition: 'all 0.12s',
  }),
  emptyMsg: {
    textAlign: 'center',
    padding: '32px 16px',
    color: '#94a3b8',
    fontSize: '14px',
  },
  loadingCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '80px 0',
    color: '#64748b',
    fontSize: '15px',
    gap: '10px',
  },
  divider: {
    height: '1px',
    background: '#e2e8f0',
    margin: '24px 0',
  },
  // Табы
  tabBar: {
    display: 'flex',
    gap: '0',
    marginBottom: '28px',
    borderBottom: '2px solid #e2e8f0',
  },
  tab: (active) => ({
    padding: '12px 28px',
    fontSize: '15px',
    fontWeight: active ? 600 : 400,
    color: active ? '#1e3a5f' : '#64748b',
    cursor: 'pointer',
    marginBottom: '-2px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.15s',
    background: 'none',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: active ? '#1e3a5f' : 'transparent',
  }),
  // Расписания
  scheduleToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  addBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: 'none',
    background: '#1e3a5f',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusBadge: (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    background: active ? '#dcfce7' : '#fef2f2',
    color: active ? '#16a34a' : '#ef4444',
  }),
  autoBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    background: '#fef3c7',
    color: '#b45309',
    fontSize: '11px',
    fontWeight: 600,
    marginLeft: '6px',
  },
  // Модал
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '560px',
    maxWidth: '95vw',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#94a3b8',
    padding: '4px',
  },
  formGroup: {
    marginBottom: '18px',
  },
  formLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '6px',
  },
  formInput: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    color: '#1e293b',
    boxSizing: 'border-box',
  },
  formSelect: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    color: '#1e293b',
    background: '#fff',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#475569',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  saveBtn: (disabled) => ({
    padding: '10px 24px',
    borderRadius: '8px',
    border: 'none',
    background: disabled ? '#94a3b8' : '#1e3a5f',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};

// Утилиты дат
function getWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDate(monday), end: formatDate(sunday) };
}

function getMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: formatDate(start), end: formatDate(end) };
}

function getQuarterRange() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  const end = new Date(now.getFullYear(), q * 3 + 3, 0);
  return { start: formatDate(start), end: formatDate(end) };
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function formatDateTime(dtStr) {
  if (!dtStr) return '-';
  try {
    const d = new Date(dtStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yy} ${hh}:${mi}`;
  } catch {
    return dtStr;
  }
}

function InformationConstructor() {
  const { t } = useTranslation(['constructor', 'common']);

  const STEPS = STEP_KEYS.map((key, i) => ({
    number: i + 1,
    label: t(`constructor:steps.${key}`),
  }));

  const SECTION_LABELS = {};
  SECTION_KEYS.forEach(key => {
    SECTION_LABELS[key] = t(`constructor:sectionLabels.${key}`);
  });

  const PERIOD_TYPES = PERIOD_TYPE_VALUES.map(v => ({
    value: v,
    label: t(`constructor:periodTypes.${v}`),
  }));

  const FORMAT_OPTIONS = FORMAT_OPTION_VALUES.map(v => ({
    value: v,
    label: t(`constructor:generation.formatOptions.${v}`),
  }));

  const SCHEDULE_TYPES = SCHEDULE_TYPE_VALUES.map(v => ({
    value: v,
    label: t(`constructor:schedule.${v}`),
  }));

  const DAY_OF_WEEK_OPTIONS = [0, 1, 2, 3, 4, 5, 6].map(v => ({
    value: v,
    label: t(`constructor:dayOfWeek.${v}`),
  }));

  const [step, setStep] = useState(1);

  // Данные
  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Выбор пользователя
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [periodType, setPeriodType] = useState('weekly');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [deptMode, setDeptMode] = useState('all'); // 'all' | 'select'
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [format, setFormat] = useState('pdf');

  // Генерация
  const [generating, setGenerating] = useState(false);
  const [generatedId, setGeneratedId] = useState(null);
  const [generatedTitle, setGeneratedTitle] = useState('');
  const [genError, setGenError] = useState('');

  // Табы
  const [activeTab, setActiveTab] = useState('constructor');

  // Расписания
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    template: '',
    schedule_type: 'weekly',
    day_of_week: 0,
    day_of_month: 1,
    department_ids: [],
    format: 'pdf',
    dept_mode: 'all',
  });
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Консолидированные отчёты
  const [conReports, setConReports] = useState([]);
  const [conLoading, setConLoading] = useState(false);
  const [conView, setConView] = useState('list'); // 'list' | 'wizard'
  const [conStep, setConStep] = useState(1);
  const [conReport, setConReport] = useState(null);
  const [conForm, setConForm] = useState({
    period_type: 'annual',
    year: new Date().getFullYear(),
    quarter: 1,
    month: 1,
    title: '',
  });
  const [conCreating, setConCreating] = useState(false);
  const [conNarratives, setConNarratives] = useState({});
  const [conIntroduction, setConIntroduction] = useState('');
  const [conSaving, setConSaving] = useState(false);
  const [conRefreshing, setConRefreshing] = useState(false);
  const [conGenerating, setConGenerating] = useState(false);
  const [conGeneratingNarratives, setConGeneratingNarratives] = useState(false);
  const [conGenFormat, setConGenFormat] = useState('word');
  const [expandedSections, setExpandedSections] = useState({});

  // Загрузка данных
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, deptRes, histRes] = await Promise.all([
        getConstructorTemplates(),
        getDepartments(),
        getGeneratedReports(),
      ]);
      setTemplates(tplRes.data.results || tplRes.data || []);
      setDepartments(deptRes.data.results || deptRes.data || []);
      setHistory(histRes.data.results || histRes.data || []);
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Обновить историю
  const refreshHistory = async () => {
    try {
      const res = await getGeneratedReports();
      setHistory(res.data.results || res.data || []);
    } catch {
      // тихо
    }
  };

  // Загрузка расписаний
  const fetchSchedules = useCallback(async () => {
    setSchedulesLoading(true);
    try {
      const res = await getScheduledReports();
      setSchedules(res.data.results || res.data || []);
    } catch (err) {
      console.error('Ошибка загрузки расписаний:', err);
    } finally {
      setSchedulesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'schedules') {
      fetchSchedules();
    }
  }, [activeTab, fetchSchedules]);

  // Загрузка консолидированных отчётов
  const fetchConsolidated = useCallback(async () => {
    setConLoading(true);
    try {
      const res = await getConsolidatedReports();
      setConReports(res.data.results || res.data || []);
    } catch (err) {
      console.error('Ошибка загрузки сводных отчётов:', err);
    } finally {
      setConLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'consolidated') {
      fetchConsolidated();
    }
  }, [activeTab, fetchConsolidated]);

  // Открыть модал создания расписания
  const openCreateSchedule = () => {
    setEditingSchedule(null);
    setScheduleForm({
      name: '',
      template: templates.length > 0 ? templates[0].id : '',
      schedule_type: 'weekly',
      day_of_week: 0,
      day_of_month: 1,
      department_ids: [],
      format: 'pdf',
      dept_mode: 'all',
    });
    setShowScheduleModal(true);
  };

  // Открыть модал редактирования
  const openEditSchedule = (sch) => {
    setEditingSchedule(sch);
    const hasDepts = sch.departments && sch.departments.length > 0;
    setScheduleForm({
      name: sch.name || '',
      template: sch.template || '',
      schedule_type: sch.schedule_type || 'weekly',
      day_of_week: sch.day_of_week != null ? sch.day_of_week : 0,
      day_of_month: sch.day_of_month != null ? sch.day_of_month : 1,
      department_ids: hasDepts ? sch.departments.map(d => d.id || d) : [],
      format: sch.format || 'pdf',
      dept_mode: hasDepts ? 'select' : 'all',
    });
    setShowScheduleModal(true);
  };

  // Сохранить расписание
  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    const payload = {
      name: scheduleForm.name,
      template: scheduleForm.template,
      schedule_type: scheduleForm.schedule_type,
      format: scheduleForm.format,
      department_ids: scheduleForm.dept_mode === 'all' ? [] : scheduleForm.department_ids,
    };
    if (scheduleForm.schedule_type === 'weekly') {
      payload.day_of_week = scheduleForm.day_of_week;
    } else {
      payload.day_of_month = scheduleForm.day_of_month;
    }

    try {
      if (editingSchedule) {
        await updateScheduledReport(editingSchedule.id, payload);
      } else {
        await createScheduledReport(payload);
      }
      setShowScheduleModal(false);
      await fetchSchedules();
    } catch (err) {
      console.error('Ошибка сохранения расписания:', err);
      const detail = err.response?.data?.detail
        || err.response?.data?.non_field_errors?.[0]
        || JSON.stringify(err.response?.data)
        || t('constructor:schedule.saveError');
      alert(detail);
    } finally {
      setScheduleSaving(false);
    }
  };

  // Переключение активности
  const handleToggleSchedule = async (id) => {
    try {
      await toggleScheduledReport(id);
      await fetchSchedules();
    } catch {
      alert(t('constructor:schedule.toggleError'));
    }
  };

  // Запуск сейчас
  const handleRunNow = async (id) => {
    if (!window.confirm(t('constructor:schedule.confirmRun'))) return;
    try {
      await runScheduledReportNow(id);
      alert(t('constructor:schedule.runSuccess'));
      await fetchSchedules();
      await refreshHistory();
    } catch {
      alert(t('constructor:schedule.runError'));
    }
  };

  // Удаление расписания
  const handleDeleteSchedule = async (id) => {
    if (!window.confirm(t('constructor:schedule.confirmDelete'))) return;
    try {
      await deleteScheduledReport(id);
      await fetchSchedules();
    } catch {
      alert(t('constructor:schedule.deleteError'));
    }
  };

  // Переключение отдела в форме расписания
  const toggleScheduleDept = (id) => {
    setScheduleForm(prev => ({
      ...prev,
      department_ids: prev.department_ids.includes(id)
        ? prev.department_ids.filter(d => d !== id)
        : [...prev.department_ids, id],
    }));
  };

  // === Консолидированные отчёты: хелперы ===

  const getConPeriodDates = (form) => {
    const y = form.year;
    let start, end;
    switch (form.period_type) {
      case 'annual':
        start = `${y}-01-01`;
        end = `${y}-12-31`;
        break;
      case 'quarterly': {
        const qStart = (form.quarter - 1) * 3;
        const s = new Date(y, qStart, 1);
        const e = new Date(y, qStart + 3, 0);
        start = formatDate(s);
        end = formatDate(e);
        break;
      }
      case 'monthly': {
        const s = new Date(y, form.month - 1, 1);
        const e = new Date(y, form.month, 0);
        start = formatDate(s);
        end = formatDate(e);
        break;
      }
      default:
        start = `${y}-01-01`;
        end = `${y}-12-31`;
    }
    return { start, end };
  };

  const getAutoTitle = (form) => {
    switch (form.period_type) {
      case 'annual':
        return `${t('constructor:consolidated.annual')} - ${form.year}`;
      case 'quarterly':
        return `Q${form.quarter} ${form.year} - ${t('constructor:consolidated.quarterly')}`;
      case 'monthly':
        return `${form.month}/${form.year} - ${t('constructor:consolidated.monthly')}`;
      default:
        return '';
    }
  };

  const handleCreateConsolidated = async () => {
    setConCreating(true);
    try {
      const dates = getConPeriodDates(conForm);
      const title = conForm.title || getAutoTitle(conForm);
      const res = await createConsolidatedReport({
        period_type: conForm.period_type,
        period_start: dates.start,
        period_end: dates.end,
        title,
      });
      const report = res.data;
      const detailRes = await getConsolidatedReport(report.id);
      setConReport(detailRes.data);
      setConNarratives(detailRes.data.narrative_data || {});
      setConIntroduction(detailRes.data.introduction_text || '');
      setConStep(2);
    } catch (err) {
      console.error('Ошибка создания сводного отчёта:', err);
      const detail = err.response?.data?.detail
        || err.response?.data?.non_field_errors?.[0]
        || JSON.stringify(err.response?.data)
        || t('constructor:consolidated.error');
      alert(detail);
    } finally {
      setConCreating(false);
    }
  };

  const handleEditConsolidated = async (id) => {
    setConLoading(true);
    try {
      const res = await getConsolidatedReport(id);
      setConReport(res.data);
      setConNarratives(res.data.narrative_data || {});
      setConIntroduction(res.data.introduction_text || '');
      setConStep(2);
      setConView('wizard');
    } catch (err) {
      console.error('Ошибка загрузки сводного отчёта:', err);
      alert(t('constructor:consolidated.error'));
    } finally {
      setConLoading(false);
    }
  };

  const handleRefreshConData = async () => {
    if (!conReport) return;
    setConRefreshing(true);
    try {
      await refreshConsolidatedData(conReport.id);
      const res = await getConsolidatedReport(conReport.id);
      setConReport(res.data);
      alert(t('constructor:consolidated.refreshed'));
    } catch {
      alert(t('constructor:consolidated.error'));
    } finally {
      setConRefreshing(false);
    }
  };

  const handleSaveNarratives = async () => {
    if (!conReport) return;
    setConSaving(true);
    try {
      await updateConsolidatedNarratives(conReport.id, {
        narratives: conNarratives,
        introduction_text: conIntroduction,
      });
      const res = await getConsolidatedReport(conReport.id);
      setConReport(res.data);
    } catch {
      alert(t('constructor:consolidated.error'));
    } finally {
      setConSaving(false);
    }
  };

  const handleGenerateNarratives = async () => {
    if (!conReport) return;
    // Проверяем есть ли уже заполненные тексты (блочный формат)
    const hasExisting = Object.values(conNarratives).some(blocks => {
      if (typeof blocks === 'string') return blocks.trim();
      if (Array.isArray(blocks)) return blocks.some(b => b.text && b.text.trim());
      return false;
    });
    let overwrite = false;
    if (hasExisting) {
      if (!window.confirm(t('constructor:consolidated.overwriteNarrativesConfirm'))) return;
      overwrite = true;
    }
    setConGeneratingNarratives(true);
    try {
      const res = await generateConsolidatedNarratives(conReport.id, { overwrite });
      setConReport(res.data);
      const updatedNarratives = res.data.narrative_data || {};
      setConNarratives(prev => ({ ...prev, ...updatedNarratives }));
      alert(t('constructor:consolidated.narrativesGenerated'));
    } catch {
      alert(t('constructor:consolidated.error'));
    } finally {
      setConGeneratingNarratives(false);
    }
  };

  const handleGenerateConDoc = async () => {
    if (!conReport) return;
    setConGenerating(true);
    try {
      await generateConsolidatedDoc(conReport.id, { format: conGenFormat });
      const res = await getConsolidatedReport(conReport.id);
      setConReport(res.data);
      alert(t('constructor:consolidated.generated'));
    } catch {
      alert(t('constructor:consolidated.error'));
    } finally {
      setConGenerating(false);
    }
  };

  const handleDownloadConDoc = async (fmt) => {
    if (!conReport) return;
    try {
      const res = await downloadConsolidatedDoc(conReport.id, fmt);
      downloadBlob(res, conReport.title || 'consolidated_report');
    } catch {
      alert(t('constructor:messages.downloadError'));
    }
  };

  const handleApproveConReport = async () => {
    if (!conReport) return;
    try {
      await approveConsolidatedReport(conReport.id);
      const res = await getConsolidatedReport(conReport.id);
      setConReport(res.data);
      alert(t('constructor:consolidated.approved'));
    } catch {
      alert(t('constructor:consolidated.error'));
    }
  };

  const handleDeleteConReport = async (id) => {
    if (!window.confirm(t('constructor:consolidated.deleteConfirm'))) return;
    try {
      await deleteConsolidatedReport(id);
      await fetchConsolidated();
    } catch {
      alert(t('constructor:consolidated.error'));
    }
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const backToConList = () => {
    setConView('list');
    setConStep(1);
    setConReport(null);
    setConNarratives({});
    setConIntroduction('');
    setExpandedSections({});
    fetchConsolidated();
  };

  // Навигация
  const canGoNext = () => {
    switch (step) {
      case 1: return selectedTemplate !== null;
      case 2: return periodType && periodStart && periodEnd;
      case 3: return deptMode === 'all' || selectedDepts.length > 0;
      case 4: return true;
      case 5: return false;
      default: return false;
    }
  };

  const goNext = () => {
    if (step < 5 && canGoNext()) setStep(step + 1);
  };
  const goBack = () => {
    if (step > 1) {
      // Сбрасываем результат генерации если возвращаемся
      if (step === 5) {
        setGeneratedId(null);
        setGeneratedTitle('');
        setGenError('');
      }
      setStep(step - 1);
    }
  };

  // Пресеты дат
  const applyPreset = (preset) => {
    let range;
    switch (preset) {
      case 'week':
        range = getWeekRange();
        setPeriodType('weekly');
        break;
      case 'month':
        range = getMonthRange();
        setPeriodType('monthly');
        break;
      case 'quarter':
        range = getQuarterRange();
        setPeriodType('quarterly');
        break;
      default: return;
    }
    setPeriodStart(range.start);
    setPeriodEnd(range.end);
  };

  // Выбор отдела
  const toggleDept = (id) => {
    setSelectedDepts((prev) =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  // Объект выбранного шаблона
  const tplObj = templates.find(t => t.id === selectedTemplate);

  // Генерация
  const handleGenerate = async () => {
    setGenerating(true);
    setGenError('');
    setGeneratedId(null);
    setGeneratedTitle('');

    const data = {
      template_id: selectedTemplate,
      period_type: periodType,
      period_start: periodStart,
      period_end: periodEnd,
      department_ids: deptMode === 'all' ? [] : selectedDepts,
      format: format,
    };

    try {
      const res = await generateConstructorReport(data);
      const report = res.data;
      setGeneratedId(report.id);
      setGeneratedTitle(report.title || t('constructor:title'));
      await refreshHistory();
    } catch (err) {
      console.error('Ошибка генерации:', err);
      setGenError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t('constructor:generation.error')
      );
    } finally {
      setGenerating(false);
    }
  };

  // Скачивание
  const handleDownload = async (id, title) => {
    try {
      const res = await downloadGeneratedReport(id);
      downloadBlob(res, title || 'report');
    } catch {
      alert(t('constructor:messages.downloadError'));
    }
  };

  // Удаление
  const handleDelete = async (id) => {
    if (!window.confirm(t('constructor:messages.deleteConfirm'))) return;
    try {
      await deleteGeneratedReport(id);
      await refreshHistory();
    } catch {
      alert(t('constructor:schedule.deleteError'));
    }
  };

  // --- Рендер шагов ---

  const renderStep1 = () => (
    <div>
      <h3 style={styles.cardTitle}>{t('constructor:templateSelection.title')}</h3>
      {templates.length === 0 ? (
        <div style={styles.emptyMsg}>{t('constructor:templateSelection.noTemplates')}</div>
      ) : (
        <div style={styles.templatesGrid}>
          {templates.map(tpl => (
            <div
              key={tpl.id}
              style={styles.templateCard(selectedTemplate === tpl.id)}
              onClick={() => setSelectedTemplate(tpl.id)}
            >
              {selectedTemplate === tpl.id && (
                <span style={styles.templateCheck}>
                  <CheckCircle size={20} />
                </span>
              )}
              <div style={styles.templateName}>{tpl.name}</div>
              <div style={styles.templateAddressee}>{tpl.addressee_display || tpl.addressee}</div>
              <div style={styles.templateDetail}>
                {tpl.detail_level_display || tpl.detail_level}
              </div>
              {tpl.description && (
                <div style={styles.templateDesc}>{tpl.description}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 style={styles.cardTitle}>{t('constructor:period.title')}</h3>
      <div style={{ ...styles.previewLabel, marginBottom: '8px' }}>{t('constructor:period.periodType')}</div>
      <div style={styles.radioRow}>
        {PERIOD_TYPES.map(pt => (
          <div
            key={pt.value}
            style={styles.radioOption(periodType === pt.value)}
            onClick={() => setPeriodType(pt.value)}
          >
            <Calendar size={16} />
            {pt.label}
          </div>
        ))}
      </div>

      <div style={styles.dateRow}>
        <div style={styles.dateGroup}>
          <span style={styles.dateLabel}>{t('constructor:period.startDate')}</span>
          <input
            type="date"
            style={styles.dateInput}
            value={periodStart}
            onChange={e => setPeriodStart(e.target.value)}
          />
        </div>
        <div style={styles.dateGroup}>
          <span style={styles.dateLabel}>{t('constructor:period.endDate')}</span>
          <input
            type="date"
            style={styles.dateInput}
            value={periodEnd}
            onChange={e => setPeriodEnd(e.target.value)}
          />
        </div>
      </div>

      <div style={{ ...styles.previewLabel, marginBottom: '6px' }}>{t('constructor:period.quickSelect')}</div>
      <div style={styles.presetBtns}>
        <button style={styles.presetBtn} onClick={() => applyPreset('week')}>
          {t('constructor:period.currentWeek')}
        </button>
        <button style={styles.presetBtn} onClick={() => applyPreset('month')}>
          {t('constructor:period.currentMonth')}
        </button>
        <button style={styles.presetBtn} onClick={() => applyPreset('quarter')}>
          {t('constructor:period.currentQuarter')}
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div>
      <h3 style={styles.cardTitle}>{t('constructor:departments.title')}</h3>
      <div style={styles.departmentModeRow}>
        <div
          style={styles.radioOption(deptMode === 'all')}
          onClick={() => { setDeptMode('all'); setSelectedDepts([]); }}
        >
          <Building size={16} />
          {t('constructor:departments.allDepartments')}
        </div>
        <div
          style={styles.radioOption(deptMode === 'select')}
          onClick={() => setDeptMode('select')}
        >
          <Building size={16} />
          {t('constructor:departments.selectDepartments')}
        </div>
      </div>

      {deptMode === 'select' && (
        <>
          <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '10px' }}>
            {t('constructor:departments.selected', { count: selectedDepts.length, total: departments.length })}
          </div>
          <div style={styles.deptList}>
            {departments.map(d => {
              const checked = selectedDepts.includes(d.id);
              return (
                <div
                  key={d.id}
                  style={styles.deptItem(checked)}
                  onClick={() => toggleDept(d.id)}
                >
                  <div style={styles.checkbox(checked)}>
                    {checked && <span style={styles.checkMark}>&#10003;</span>}
                  </div>
                  {d.name_ru || d.name}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  const renderStep4 = () => {
    const sections = tplObj?.sections || [];
    const deptNames = deptMode === 'all'
      ? t('constructor:preview.allDepartments')
      : selectedDepts
          .map(id => {
            const d = departments.find(dep => dep.id === id);
            return d ? (d.name_ru || d.name) : id;
          })
          .join(', ');

    const periodLabel = PERIOD_TYPES.find(p => p.value === periodType)?.label || periodType;

    return (
      <div>
        <h3 style={styles.cardTitle}>
          <Eye size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('constructor:preview.title')}
        </h3>

        <div style={styles.previewSection}>
          <div style={styles.previewLabel}>{t('constructor:preview.template')}</div>
          <div style={styles.previewValue}>{tplObj?.name || '-'}</div>
        </div>

        <div style={styles.previewSection}>
          <div style={styles.previewLabel}>{t('constructor:preview.period')}</div>
          <div style={styles.previewValue}>
            {periodLabel}: {formatDisplayDate(periodStart)} &mdash; {formatDisplayDate(periodEnd)}
          </div>
        </div>

        <div style={styles.previewSection}>
          <div style={styles.previewLabel}>{t('constructor:preview.departments')}</div>
          <div style={styles.previewValue}>{deptNames}</div>
        </div>

        <div style={styles.divider} />

        <div style={styles.previewSection}>
          <div style={styles.previewLabel}>{t('constructor:preview.sections')}</div>
          {sections.length > 0 ? (
            <div style={styles.sectionsList}>
              {sections.map((sec) => (
                <span key={sec} style={styles.sectionChip}>
                  {SECTION_LABELS[sec] || sec}
                </span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '8px' }}>
              {t('constructor:preview.noSections')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStep5 = () => (
    <div>
      <h3 style={styles.cardTitle}>{t('constructor:generation.title')}</h3>

      <div style={{ ...styles.previewLabel, marginBottom: '8px' }}>{t('constructor:generation.format')}</div>
      <div style={{ ...styles.radioRow, marginBottom: '28px' }}>
        {FORMAT_OPTIONS.map(f => (
          <div
            key={f.value}
            style={styles.radioOption(format === f.value)}
            onClick={() => setFormat(f.value)}
          >
            <FileText size={16} />
            {f.label}
          </div>
        ))}
      </div>

      <div style={styles.genCenter}>
        {!generatedId && !generating && (
          <button
            style={styles.genBtn(false)}
            onClick={handleGenerate}
          >
            <FileText size={18} />
            {t('constructor:generation.generate')}
          </button>
        )}

        {generating && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#64748b', fontSize: '15px' }}>
            <Loader size={20} className="spinning" style={{ animation: 'spin 1s linear infinite' }} />
            {t('constructor:generation.generating')}
          </div>
        )}

        {genError && (
          <div style={{ color: '#ef4444', fontSize: '14px', textAlign: 'center' }}>
            {genError}
          </div>
        )}

        {generatedId && (
          <>
            <div style={styles.successMsg}>
              <CheckCircle size={20} />
              {t('constructor:generation.success')}
            </div>
            <button
              style={styles.downloadBtn}
              onClick={() => handleDownload(generatedId, generatedTitle)}
            >
              <Download size={18} />
              {t('constructor:generation.download')}
            </button>
          </>
        )}
      </div>
    </div>
  );

  // --- Рендер истории ---
  const renderHistory = () => {
    const formatMap = { pdf: 'PDF', word: 'Word', pptx: 'PPTX' };

    return (
      <div style={styles.historySection}>
        <div style={styles.historyTitle}>
          <FileText size={22} />
          {t('constructor:history.title')}
        </div>

        <div style={styles.card}>
          {history.length === 0 ? (
            <div style={styles.emptyMsg}>{t('constructor:history.noReports')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t('constructor:history.heading')}</th>
                    <th style={styles.th}>{t('constructor:history.template')}</th>
                    <th style={styles.th}>{t('constructor:history.period')}</th>
                    <th style={styles.th}>{t('constructor:history.format')}</th>
                    <th style={styles.th}>{t('constructor:history.date')}</th>
                    <th style={styles.th}>{t('constructor:history.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(item => (
                    <tr key={item.id}>
                      <td style={styles.td}>
                        {item.title || '-'}
                        {item.is_auto_generated && (
                          <span style={styles.autoBadge}>{t('constructor:history.auto')}</span>
                        )}
                      </td>
                      <td style={styles.td}>{item.template_name || item.template?.name || '-'}</td>
                      <td style={styles.td}>
                        {formatDisplayDate(item.period_start)} &mdash; {formatDisplayDate(item.period_end)}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          background: '#f0f5ff',
                          color: '#1e3a5f',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}>
                          {formatMap[item.format] || item.format || '-'}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDateTime(item.created_at)}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.actionBtn('#1e3a5f')}
                          title={t('common:buttons.download')}
                          onClick={() => handleDownload(item.id, item.title || 'report')}
                        >
                          <Download size={16} />
                        </button>
                        <button
                          style={styles.actionBtn('#ef4444')}
                          title={t('common:buttons.delete')}
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Рендер вкладки расписаний ---
  const renderSchedulesTab = () => {
    const scheduleTypeLabels = {};
    SCHEDULE_TYPE_VALUES.forEach(v => { scheduleTypeLabels[v] = t(`constructor:schedule.${v}`); });
    const formatLabels = { pdf: 'PDF', word: 'Word' };

    const getDayLabel = (sch) => {
      if (sch.schedule_type === 'weekly') {
        const day = DAY_OF_WEEK_OPTIONS.find(d => d.value === sch.day_of_week);
        return day ? day.label : '-';
      }
      return sch.day_of_month ? t('constructor:schedule.dayNumber', { day: sch.day_of_month }) : '-';
    };

    return (
      <div>
        <div style={styles.scheduleToolbar}>
          <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>{t('constructor:schedule.autoGenTitle')}</h3>
          <button style={styles.addBtn} onClick={openCreateSchedule}>
            <Plus size={16} /> {t('constructor:schedule.create')}
          </button>
        </div>

        <div style={styles.card}>
          {schedulesLoading ? (
            <div style={styles.loadingCenter}>
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              {t('constructor:schedule.loading')}
            </div>
          ) : schedules.length === 0 ? (
            <div style={styles.emptyMsg}>
              {t('constructor:schedule.noSchedules')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.name')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.template')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.frequency')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.day')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.format')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.status')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.lastGenerated')}</th>
                    <th style={styles.th}>{t('constructor:schedule.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(sch => (
                    <tr key={sch.id}>
                      <td style={styles.td}>{sch.name}</td>
                      <td style={styles.td}>{sch.template_name || '-'}</td>
                      <td style={styles.td}>{scheduleTypeLabels[sch.schedule_type] || sch.schedule_type}</td>
                      <td style={styles.td}>{getDayLabel(sch)}</td>
                      <td style={styles.td}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '12px',
                          background: '#f0f5ff', color: '#1e3a5f',
                          fontSize: '12px', fontWeight: 600,
                        }}>
                          {formatLabels[sch.format] || sch.format}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.statusBadge(sch.is_active)}>
                          <Power size={12} />
                          {sch.is_active ? t('constructor:schedule.active') : t('constructor:schedule.inactive')}
                        </span>
                      </td>
                      <td style={styles.td}>{formatDateTime(sch.last_generated_at)}</td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <button
                            style={styles.actionBtn(sch.is_active ? '#f59e0b' : '#16a34a')}
                            title={sch.is_active ? t('constructor:schedule.inactive') : t('constructor:schedule.active')}
                            onClick={() => handleToggleSchedule(sch.id)}
                          >
                            <Power size={15} />
                          </button>
                          <button
                            style={styles.actionBtn('#3b82f6')}
                            title={t('constructor:schedule.confirmRun')}
                            onClick={() => handleRunNow(sch.id)}
                          >
                            <Play size={15} />
                          </button>
                          <button
                            style={styles.actionBtn('#64748b')}
                            title={t('common:buttons.edit')}
                            onClick={() => openEditSchedule(sch)}
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            style={styles.actionBtn('#ef4444')}
                            title={t('common:buttons.delete')}
                            onClick={() => handleDeleteSchedule(sch.id)}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- Модал расписания ---
  const renderScheduleModal = () => {
    if (!showScheduleModal) return null;

    const canSave = scheduleForm.name.trim() && scheduleForm.template
      && (scheduleForm.dept_mode === 'all' || scheduleForm.department_ids.length > 0);

    return (
      <div style={styles.modalOverlay} onClick={() => setShowScheduleModal(false)}>
        <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h3 style={styles.modalTitle}>
              {editingSchedule ? t('constructor:schedule.edit') : t('constructor:schedule.create')}
            </h3>
            <button style={styles.closeBtn} onClick={() => setShowScheduleModal(false)}>
              <X size={20} />
            </button>
          </div>

          {/* Название */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:schedule.name')}</label>
            <input
              style={styles.formInput}
              value={scheduleForm.name}
              onChange={e => setScheduleForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t('constructor:schedule.namePlaceholder')}
            />
          </div>

          {/* Шаблон */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:schedule.templateLabel')}</label>
            <select
              style={styles.formSelect}
              value={scheduleForm.template}
              onChange={e => setScheduleForm(f => ({ ...f, template: Number(e.target.value) }))}
            >
              <option value="">{t('constructor:schedule.selectTemplate')}</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Периодичность */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:schedule.frequency')}</label>
            <div style={styles.radioRow}>
              {SCHEDULE_TYPES.map(st => (
                <div
                  key={st.value}
                  style={styles.radioOption(scheduleForm.schedule_type === st.value)}
                  onClick={() => setScheduleForm(f => ({ ...f, schedule_type: st.value }))}
                >
                  <Clock size={14} /> {st.label}
                </div>
              ))}
            </div>
          </div>

          {/* День */}
          <div style={styles.formGroup}>
            {scheduleForm.schedule_type === 'weekly' ? (
              <>
                <label style={styles.formLabel}>{t('constructor:schedule.dayOfWeek')}</label>
                <select
                  style={styles.formSelect}
                  value={scheduleForm.day_of_week}
                  onChange={e => setScheduleForm(f => ({ ...f, day_of_week: Number(e.target.value) }))}
                >
                  {DAY_OF_WEEK_OPTIONS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label style={styles.formLabel}>{t('constructor:schedule.dayOfMonth')}</label>
                <input
                  type="number"
                  min={1}
                  max={28}
                  style={styles.formInput}
                  value={scheduleForm.day_of_month}
                  onChange={e => setScheduleForm(f => ({ ...f, day_of_month: Number(e.target.value) }))}
                />
              </>
            )}
          </div>

          {/* Отделы */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:schedule.departments')}</label>
            <div style={styles.departmentModeRow}>
              <div
                style={styles.radioOption(scheduleForm.dept_mode === 'all')}
                onClick={() => setScheduleForm(f => ({ ...f, dept_mode: 'all', department_ids: [] }))}
              >
                <Building size={14} /> {t('constructor:schedule.allDepartments')}
              </div>
              <div
                style={styles.radioOption(scheduleForm.dept_mode === 'select')}
                onClick={() => setScheduleForm(f => ({ ...f, dept_mode: 'select' }))}
              >
                <Building size={14} /> {t('constructor:schedule.selectDepts')}
              </div>
            </div>
            {scheduleForm.dept_mode === 'select' && (
              <div style={{ ...styles.deptList, maxHeight: '200px' }}>
                {departments.map(d => {
                  const checked = scheduleForm.department_ids.includes(d.id);
                  return (
                    <div
                      key={d.id}
                      style={styles.deptItem(checked)}
                      onClick={() => toggleScheduleDept(d.id)}
                    >
                      <div style={styles.checkbox(checked)}>
                        {checked && <span style={styles.checkMark}>&#10003;</span>}
                      </div>
                      {d.name_ru || d.name}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Формат */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:schedule.fileFormat')}</label>
            <div style={{ ...styles.radioRow, marginBottom: 0 }}>
              {FORMAT_OPTIONS.map(f => (
                <div
                  key={f.value}
                  style={styles.radioOption(scheduleForm.format === f.value)}
                  onClick={() => setScheduleForm(frm => ({ ...frm, format: f.value }))}
                >
                  <FileText size={14} /> {f.label}
                </div>
              ))}
            </div>
          </div>

          {/* Кнопки */}
          <div style={styles.modalActions}>
            <button style={styles.cancelBtn} onClick={() => setShowScheduleModal(false)}>
              {t('common:buttons.cancel')}
            </button>
            <button
              style={styles.saveBtn(!canSave || scheduleSaving)}
              disabled={!canSave || scheduleSaving}
              onClick={handleSaveSchedule}
            >
              {scheduleSaving ? t('constructor:schedule.saving') : (editingSchedule ? t('constructor:schedule.save') : t('constructor:schedule.create'))}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- Рендер вкладки сводных отчётов ---

  const renderConsolidatedTab = () => {
    if (conView === 'wizard') return renderConsolidatedWizard();
    return renderConsolidatedList();
  };

  const renderConsolidatedList = () => {
    const statusColors = {
      draft: { bg: '#fef3c7', color: '#b45309' },
      in_review: { bg: '#dbeafe', color: '#1d4ed8' },
      approved: { bg: '#dcfce7', color: '#16a34a' },
    };
    const periodTypeLabels = {
      annual: t('constructor:consolidated.annual'),
      quarterly: t('constructor:consolidated.quarterly'),
      monthly: t('constructor:consolidated.monthly'),
    };

    return (
      <div>
        <div style={styles.scheduleToolbar}>
          <h3 style={{ ...styles.cardTitle, marginBottom: 0 }}>{t('constructor:consolidated.title')}</h3>
          <button style={styles.addBtn} onClick={() => { setConView('wizard'); setConStep(1); }}>
            <Plus size={16} /> {t('constructor:consolidated.create')}
          </button>
        </div>

        <div style={styles.card}>
          {conLoading ? (
            <div style={styles.loadingCenter}>
              <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
              {t('constructor:loading')}
            </div>
          ) : conReports.length === 0 ? (
            <div style={styles.emptyMsg}>{t('constructor:consolidated.noReports')}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t('constructor:consolidated.tableHeaders.title')}</th>
                    <th style={styles.th}>{t('constructor:consolidated.tableHeaders.type')}</th>
                    <th style={styles.th}>{t('constructor:consolidated.tableHeaders.period')}</th>
                    <th style={styles.th}>{t('constructor:consolidated.tableHeaders.status')}</th>
                    <th style={styles.th}>{t('constructor:consolidated.tableHeaders.created')}</th>
                    <th style={styles.th}>{t('constructor:consolidated.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conReports.map(r => {
                    const sc = statusColors[r.status] || statusColors.draft;
                    return (
                      <tr key={r.id}>
                        <td style={styles.td}>{r.title}</td>
                        <td style={styles.td}>{periodTypeLabels[r.period_type] || r.period_type}</td>
                        <td style={styles.td}>
                          {formatDisplayDate(r.period_start)} &mdash; {formatDisplayDate(r.period_end)}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                            background: sc.bg, color: sc.color,
                          }}>
                            {t(`constructor:consolidated.status.${r.status}`)}
                          </span>
                        </td>
                        <td style={styles.td}>{formatDateTime(r.created_at)}</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '2px' }}>
                            <button
                              style={styles.actionBtn('#1e3a5f')}
                              title={t('common:buttons.edit')}
                              onClick={() => handleEditConsolidated(r.id)}
                            >
                              <Edit2 size={15} />
                            </button>
                            <button
                              style={styles.actionBtn('#ef4444')}
                              title={t('common:buttons.delete')}
                              onClick={() => handleDeleteConReport(r.id)}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderConsolidatedWizard = () => {
    const wizardSteps = [
      t('constructor:consolidated.wizard.step1'),
      t('constructor:consolidated.wizard.step2'),
      t('constructor:consolidated.wizard.step3'),
      t('constructor:consolidated.wizard.step4'),
    ];

    const conStepRenderers = {
      1: renderConStep1,
      2: renderConStep2,
      3: renderConStep3,
      4: renderConStep4,
    };

    return (
      <div>
        <button
          style={{ ...styles.actionBtn('#64748b'), marginBottom: '16px', fontSize: '14px' }}
          onClick={backToConList}
        >
          <ChevronLeft size={16} /> {t('constructor:consolidated.backToList')}
        </button>

        <div style={styles.stepsBar}>
          {wizardSteps.map((label, i) => {
            const num = i + 1;
            const active = num === conStep;
            const completed = num < conStep;
            return (
              <React.Fragment key={num}>
                <div style={styles.stepItem(active, completed)}>
                  <div style={styles.stepCircle(active, completed)}>
                    {completed ? <CheckCircle size={18} /> : num}
                  </div>
                  <span style={styles.stepLabel(active)}>{label}</span>
                </div>
                {i < wizardSteps.length - 1 && <div style={styles.stepConnector} />}
              </React.Fragment>
            );
          })}
        </div>

        <div style={styles.card}>
          {conStepRenderers[conStep]()}
        </div>
      </div>
    );
  };

  const renderConStep1 = () => {
    const currentYear = new Date().getFullYear();
    const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
    const quarters = [1, 2, 3, 4];
    const months = Array.from({ length: 12 }, (_, i) => i + 1);

    return (
      <div>
        <h3 style={styles.cardTitle}>{t('constructor:consolidated.wizard.step1')}</h3>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>{t('constructor:consolidated.periodType')}</label>
          <div style={styles.radioRow}>
            {['annual', 'quarterly', 'monthly'].map(pt => (
              <div
                key={pt}
                style={styles.radioOption(conForm.period_type === pt)}
                onClick={() => setConForm(f => ({ ...f, period_type: pt }))}
              >
                <Calendar size={16} />
                {t(`constructor:consolidated.${pt}`)}
              </div>
            ))}
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>{t('constructor:consolidated.year')}</label>
          <select
            style={styles.formSelect}
            value={conForm.year}
            onChange={e => setConForm(f => ({ ...f, year: Number(e.target.value) }))}
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {conForm.period_type === 'quarterly' && (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:consolidated.quarter')}</label>
            <div style={styles.radioRow}>
              {quarters.map(q => (
                <div
                  key={q}
                  style={styles.radioOption(conForm.quarter === q)}
                  onClick={() => setConForm(f => ({ ...f, quarter: q }))}
                >
                  Q{q}
                </div>
              ))}
            </div>
          </div>
        )}

        {conForm.period_type === 'monthly' && (
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t('constructor:consolidated.month')}</label>
            <select
              style={styles.formSelect}
              value={conForm.month}
              onChange={e => setConForm(f => ({ ...f, month: Number(e.target.value) }))}
            >
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>{t('constructor:consolidated.tableHeaders.title')}</label>
          <input
            style={styles.formInput}
            value={conForm.title || getAutoTitle(conForm)}
            onChange={e => setConForm(f => ({ ...f, title: e.target.value }))}
          />
        </div>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button
            style={styles.genBtn(conCreating)}
            disabled={conCreating}
            onClick={handleCreateConsolidated}
          >
            {conCreating ? (
              <>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                {t('constructor:consolidated.creating')}
              </>
            ) : (
              <>
                <BarChart2 size={18} />
                {t('constructor:consolidated.createAndLoad')}
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderConStep2 = () => {
    if (!conReport) return null;
    const sections = conReport.sections_config || [];
    const sectionsData = conReport.sections_data || {};

    return (
      <div>
        <h3 style={styles.cardTitle}>{t('constructor:consolidated.wizard.step2')}</h3>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            style={{ ...styles.presetBtn, display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={handleRefreshConData}
            disabled={conRefreshing}
          >
            <RefreshCw size={14} style={conRefreshing ? { animation: 'spin 1s linear infinite' } : {}} />
            {conRefreshing ? t('constructor:consolidated.refreshing') : t('constructor:consolidated.refreshData')}
          </button>
          <button
            style={{ ...styles.presetBtn, display: 'flex', alignItems: 'center', gap: '6px', background: '#eff6ff', borderColor: '#3b82f6', color: '#1e3a5f' }}
            onClick={handleSaveNarratives}
            disabled={conSaving}
          >
            {conSaving ? t('constructor:consolidated.saving') : t('constructor:consolidated.saveNarratives')}
          </button>
          <button
            style={{ ...styles.presetBtn, display: 'flex', alignItems: 'center', gap: '6px', background: '#fefce8', borderColor: '#eab308', color: '#854d0e' }}
            onClick={handleGenerateNarratives}
            disabled={conGeneratingNarratives}
          >
            <BarChart2 size={14} />
            {conGeneratingNarratives ? t('constructor:consolidated.generatingNarratives') : t('constructor:consolidated.generateNarratives')}
          </button>
        </div>

        {/* Вступительный текст */}
        <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', background: '#f8fafc' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e3a5f', marginBottom: '8px' }}>
            {t('constructor:consolidated.introduction')}
          </div>
          <textarea
            style={{
              ...styles.formInput,
              minHeight: '150px',
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: '1.5',
            }}
            value={conIntroduction}
            onChange={e => setConIntroduction(e.target.value)}
            placeholder={t('constructor:consolidated.introductionPlaceholder')}
          />
        </div>

        <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '12px' }}>
          {t('constructor:consolidated.sections')}
        </div>

        {sections.map(section => {
          const sKey = section.key;
          const expanded = expandedSections[sKey];
          const sd = sectionsData[sKey] || {};
          const numbers = sd.numbers || {};
          const hasData = sd.has_data || Object.values(numbers).some(n => n.value > 0);
          const blocksConfig = section.narrative_blocks || [];
          // Получить текущие блоки для этого раздела
          const currentBlocks = (() => {
            const raw = conNarratives[sKey];
            if (Array.isArray(raw)) return raw;
            if (typeof raw === 'string' && raw.trim()) return [{ id: 'legacy', text: raw }];
            return [];
          })();
          const hasText = currentBlocks.some(b => b.text && b.text.trim());
          const sectionTitle = t(`constructor:consolidated.sectionTitles.${sKey}`, { defaultValue: section.title_ru });

          // Хелпер: собрать полный список блоков (конфиг + пользовательские)
          const mergedBlocks = (() => {
            const result = [];
            const currentById = {};
            currentBlocks.forEach(b => { currentById[b.id] = b; });
            // Сначала блоки из конфига
            blocksConfig.forEach(cfg => {
              const saved = currentById[cfg.id];
              result.push({
                id: cfg.id,
                type: cfg.type,
                label: cfg.label,
                hint: cfg.hint || '',
                template: cfg.template || '',
                text: saved ? saved.text || '' : '',
              });
              delete currentById[cfg.id];
            });
            // Добавить пользовательские блоки (не из конфига)
            Object.entries(currentById).forEach(([id, b]) => {
              if (id !== 'legacy' || b.text?.trim()) {
                result.push({
                  id,
                  type: 'manual',
                  label: id === 'legacy' ? t('constructor:consolidated.narrative') : `${t('constructor:blocks.manual')} #${result.length + 1}`,
                  hint: '',
                  template: '',
                  text: b.text || '',
                });
              }
            });
            return result;
          })();

          // Обновить блок текста
          const updateBlockText = (blockId, newText) => {
            setConNarratives(prev => {
              const prevBlocks = Array.isArray(prev[sKey]) ? [...prev[sKey]] : [];
              const idx = prevBlocks.findIndex(b => b.id === blockId);
              if (idx >= 0) {
                prevBlocks[idx] = { ...prevBlocks[idx], text: newText };
              } else {
                prevBlocks.push({ id: blockId, text: newText });
              }
              return { ...prev, [sKey]: prevBlocks };
            });
          };

          // Удалить блок
          const removeBlock = (blockId) => {
            if (!window.confirm(t('constructor:blocks.removeConfirm'))) return;
            setConNarratives(prev => {
              const prevBlocks = Array.isArray(prev[sKey]) ? prev[sKey].filter(b => b.id !== blockId) : [];
              return { ...prev, [sKey]: prevBlocks };
            });
          };

          // Переместить блок
          const moveBlock = (blockId, direction) => {
            setConNarratives(prev => {
              const prevBlocks = Array.isArray(prev[sKey]) ? [...prev[sKey]] : mergedBlocks.map(b => ({ id: b.id, text: b.text }));
              const idx = prevBlocks.findIndex(b => b.id === blockId);
              const newIdx = idx + direction;
              if (newIdx < 0 || newIdx >= prevBlocks.length) return prev;
              [prevBlocks[idx], prevBlocks[newIdx]] = [prevBlocks[newIdx], prevBlocks[idx]];
              return { ...prev, [sKey]: prevBlocks };
            });
          };

          // Добавить ручной блок
          const addManualBlock = () => {
            const newId = `custom_${Date.now()}`;
            setConNarratives(prev => {
              const prevBlocks = Array.isArray(prev[sKey]) ? [...prev[sKey]] : mergedBlocks.map(b => ({ id: b.id, text: b.text }));
              prevBlocks.push({ id: newId, text: '' });
              return { ...prev, [sKey]: prevBlocks };
            });
          };

          return (
            <div key={sKey} style={{ marginBottom: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 16px', background: expanded ? '#f8fafc' : '#fff',
                  cursor: 'pointer', userSelect: 'none',
                }}
                onClick={() => toggleSection(sKey)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
                    {section.number}. {sectionTitle}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                    background: hasData ? '#dcfce7' : '#fef2f2',
                    color: hasData ? '#16a34a' : '#ef4444',
                  }}>
                    {hasData ? t('constructor:consolidated.hasData') : t('constructor:consolidated.noData')}
                  </span>
                  <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600,
                    background: hasText ? '#dbeafe' : '#fef2f2',
                    color: hasText ? '#1d4ed8' : '#ef4444',
                  }}>
                    {hasText ? t('constructor:consolidated.hasText') : t('constructor:consolidated.noText')}
                  </span>
                </div>
                {expanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
              </div>

              {expanded && (
                <div style={{ padding: '16px', borderTop: '1px solid #e2e8f0' }}>
                  {/* Числовые данные */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>
                      {t('constructor:consolidated.sectionData')}
                    </div>
                    {Object.keys(numbers).length > 0 ? (
                      <table style={{ ...styles.table, fontSize: '13px' }}>
                        <thead>
                          <tr>
                            <th style={{ ...styles.th, padding: '8px 12px' }}>{t('constructor:consolidated.indicator')}</th>
                            <th style={{ ...styles.th, padding: '8px 12px', textAlign: 'right' }}>{t('constructor:consolidated.value')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(numbers).map(([compositeKey, info]) => (
                            <tr key={compositeKey}>
                              <td style={{ ...styles.td, padding: '6px 12px' }}>{info.label || compositeKey}</td>
                              <td style={{ ...styles.td, padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>
                                {typeof info.value === 'number' ? info.value.toLocaleString() : info.value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>
                        {t('constructor:consolidated.noData')}
                      </div>
                    )}
                  </div>

                  {/* Блоки нарративов */}
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>
                    {t('constructor:consolidated.narrative')}
                  </div>
                  {mergedBlocks.map((block, idx) => {
                    const isAuto = block.type === 'auto';
                    const borderColor = isAuto ? '#86efac' : '#fde68a';
                    const bgColor = isAuto ? '#f0fdf4' : '#fffbeb';
                    const labelColor = isAuto ? '#15803d' : '#92400e';
                    const typeLabel = isAuto ? t('constructor:blocks.auto') : t('constructor:blocks.manual');
                    const isConfigBlock = blocksConfig.some(c => c.id === block.id);

                    return (
                      <div key={block.id} style={{
                        border: `1px solid ${borderColor}`,
                        borderRadius: '8px',
                        marginBottom: '8px',
                        background: bgColor,
                        overflow: 'hidden',
                      }}>
                        {/* Заголовок блока */}
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderBottom: `1px solid ${borderColor}`,
                          background: isAuto ? '#dcfce7' : '#fef3c7',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 700,
                              background: isAuto ? '#15803d' : '#92400e',
                              color: '#fff',
                            }}>
                              {typeLabel}
                            </span>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: labelColor }}>
                              {block.label}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button
                              title={t('constructor:blocks.moveUp')}
                              disabled={idx === 0}
                              onClick={() => moveBlock(block.id, -1)}
                              style={{ border: 'none', background: 'none', cursor: idx === 0 ? 'default' : 'pointer', padding: '2px', opacity: idx === 0 ? 0.3 : 1 }}
                            >
                              <ChevronUp size={14} />
                            </button>
                            <button
                              title={t('constructor:blocks.moveDown')}
                              disabled={idx === mergedBlocks.length - 1}
                              onClick={() => moveBlock(block.id, 1)}
                              style={{ border: 'none', background: 'none', cursor: idx === mergedBlocks.length - 1 ? 'default' : 'pointer', padding: '2px', opacity: idx === mergedBlocks.length - 1 ? 0.3 : 1 }}
                            >
                              <ChevronDown size={14} />
                            </button>
                            {!isConfigBlock && (
                              <button
                                title={t('constructor:blocks.removeBlock')}
                                onClick={() => removeBlock(block.id)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '2px', color: '#ef4444' }}
                              >
                                <X size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Тело блока */}
                        <div style={{ padding: '10px 12px' }}>
                          {block.hint && !block.text.trim() && (
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px', fontStyle: 'italic' }}>
                              {t('constructor:blocks.hint')}: {block.hint}
                            </div>
                          )}
                          <textarea
                            style={{
                              ...styles.formInput,
                              minHeight: block.text.trim() ? '80px' : '60px',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: '1.5',
                              fontSize: '13px',
                              background: '#fff',
                            }}
                            value={block.text}
                            onChange={e => updateBlockText(block.id, e.target.value)}
                            placeholder={block.hint || t('constructor:consolidated.narrativePlaceholder')}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Кнопка добавления ручного блока */}
                  <button
                    onClick={addManualBlock}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      border: '1px dashed #cbd5e1', borderRadius: '8px',
                      padding: '8px 14px', background: '#fff', cursor: 'pointer',
                      color: '#64748b', fontSize: '13px', width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <Plus size={14} /> {t('constructor:blocks.addBlock')}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <div style={styles.divider} />
        <div style={styles.navRow}>
          <button style={styles.navBtn(false)} onClick={backToConList}>
            <ChevronLeft size={18} /> {t('constructor:consolidated.backToList')}
          </button>
          <button style={styles.navBtn(false)} onClick={() => { handleSaveNarratives(); setConStep(3); }}>
            {t('constructor:nav.next')} <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  };

  const renderConStep3 = () => {
    if (!conReport) return null;
    const sections = conReport.sections_config || [];
    const sectionsData = conReport.sections_data || {};

    return (
      <div>
        <h3 style={styles.cardTitle}>
          <Eye size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          {t('constructor:consolidated.preview')}
        </h3>

        <div style={styles.previewSection}>
          <div style={styles.previewLabel}>{t('constructor:consolidated.tableHeaders.title')}</div>
          <div style={styles.previewValue}>{conReport.title}</div>
        </div>
        <div style={styles.previewSection}>
          <div style={styles.previewLabel}>{t('constructor:consolidated.tableHeaders.period')}</div>
          <div style={styles.previewValue}>
            {formatDisplayDate(conReport.period_start)} &mdash; {formatDisplayDate(conReport.period_end)}
          </div>
        </div>

        {/* Вступительный текст в предпросмотре */}
        {conIntroduction && conIntroduction.trim() && (
          <div style={styles.previewSection}>
            <div style={styles.previewLabel}>{t('constructor:consolidated.introduction')}</div>
            <div style={{ ...styles.previewValue, fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
              {conIntroduction.length > 300 ? conIntroduction.substring(0, 300) + '...' : conIntroduction}
            </div>
          </div>
        )}

        <div style={styles.divider} />

        {sections.map(section => {
          const sKey = section.key;
          const sd = sectionsData[sKey] || {};
          const numbers = sd.numbers || {};
          const hasData = Object.values(numbers).some(n => n.value > 0);
          const rawNarr = conNarratives[sKey];
          const blocks = Array.isArray(rawNarr) ? rawNarr : (typeof rawNarr === 'string' && rawNarr.trim() ? [{ id: 'legacy', text: rawNarr }] : []);
          const hasText = blocks.some(b => b.text && b.text.trim());
          const sectionTitle = t(`constructor:consolidated.sectionTitles.${sKey}`, { defaultValue: section.title_ru });

          return (
            <div key={sKey} style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
              <div style={{ fontWeight: 600, color: '#1e3a5f', fontSize: '14px', marginBottom: '8px' }}>
                {section.number}. {sectionTitle}
              </div>
              {hasData && (
                <div style={{ fontSize: '13px', color: '#475569', marginBottom: '6px', lineHeight: '1.6' }}>
                  {Object.entries(numbers).map(([compositeKey, info]) =>
                    info.value > 0 ? (
                      <span key={compositeKey} style={{ marginRight: '16px' }}>
                        {info.label}: <strong>{info.value.toLocaleString()}</strong>
                      </span>
                    ) : null
                  )}
                </div>
              )}
              {hasText && (
                <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                  {blocks.filter(b => b.text && b.text.trim()).map((block, i) => (
                    <p key={block.id || i} style={{ margin: '0 0 6px 0', whiteSpace: 'pre-wrap' }}>
                      {block.text.length > 200 ? block.text.substring(0, 200) + '...' : block.text}
                    </p>
                  ))}
                </div>
              )}
              {!hasData && !hasText && (
                <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                  {t('constructor:consolidated.noData')}
                </div>
              )}
            </div>
          );
        })}

        <div style={styles.divider} />
        <div style={styles.navRow}>
          <button style={styles.navBtn(false)} onClick={() => setConStep(2)}>
            <ChevronLeft size={18} /> {t('constructor:nav.back')}
          </button>
          <button style={styles.navBtn(false)} onClick={() => setConStep(4)}>
            {t('constructor:consolidated.generate')} <ChevronRight size={18} />
          </button>
        </div>
      </div>
    );
  };

  const renderConStep4 = () => {
    if (!conReport) return null;

    return (
      <div>
        <h3 style={styles.cardTitle}>{t('constructor:consolidated.wizard.step4')}</h3>

        <div style={{ ...styles.previewLabel, marginBottom: '8px' }}>{t('constructor:generation.format')}</div>
        <div style={{ ...styles.radioRow, marginBottom: '28px' }}>
          {[
            { value: 'word', label: t('constructor:consolidated.formatWord') },
            { value: 'pdf', label: t('constructor:consolidated.formatPdf') },
            { value: 'both', label: t('constructor:consolidated.formatBoth') },
          ].map(f => (
            <div
              key={f.value}
              style={styles.radioOption(conGenFormat === f.value)}
              onClick={() => setConGenFormat(f.value)}
            >
              <FileText size={16} /> {f.label}
            </div>
          ))}
        </div>

        <div style={styles.genCenter}>
          <button
            style={styles.genBtn(conGenerating)}
            disabled={conGenerating}
            onClick={handleGenerateConDoc}
          >
            {conGenerating ? (
              <>
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                {t('constructor:consolidated.generating')}
              </>
            ) : (
              <>
                <FileText size={18} />
                {t('constructor:consolidated.generate')}
              </>
            )}
          </button>

          {conReport.has_word && (
            <button style={styles.downloadBtn} onClick={() => handleDownloadConDoc('word')}>
              <Download size={18} /> {t('constructor:consolidated.downloadWord')}
            </button>
          )}
          {conReport.has_pdf && (
            <button style={{ ...styles.downloadBtn, background: '#dc2626' }} onClick={() => handleDownloadConDoc('pdf')}>
              <Download size={18} /> {t('constructor:consolidated.downloadPdf')}
            </button>
          )}

          {conReport.status !== 'approved' && (
            <button
              style={{ ...styles.downloadBtn, background: '#7c3aed' }}
              onClick={handleApproveConReport}
            >
              <Award size={18} /> {t('constructor:consolidated.approve')}
            </button>
          )}
          {conReport.status === 'approved' && (
            <div style={styles.successMsg}>
              <CheckCircle size={20} /> {t('constructor:consolidated.approved')}
            </div>
          )}
        </div>

        <div style={styles.divider} />
        <div style={styles.navRow}>
          <button style={styles.navBtn(false)} onClick={() => setConStep(3)}>
            <ChevronLeft size={18} /> {t('constructor:nav.back')}
          </button>
          <button style={styles.navBtn(false)} onClick={backToConList}>
            {t('constructor:consolidated.backToList')}
          </button>
        </div>
      </div>
    );
  };

  // --- Основной рендер ---

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingCenter}>
          <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
          {t('constructor:loading')}
        </div>
      </div>
    );
  }

  const stepRenderers = {
    1: renderStep1,
    2: renderStep2,
    3: renderStep3,
    4: renderStep4,
    5: renderStep5,
  };

  return (
    <div style={styles.page}>
      {/* Анимация для спиннера */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Заголовок */}
      <div style={styles.header}>
        <FileText size={28} color="#1e3a5f" />
        <h1 style={styles.headerTitle}>{t('constructor:title')}</h1>
      </div>

      {/* Табы */}
      <div style={styles.tabBar}>
        <button
          style={styles.tab(activeTab === 'constructor')}
          onClick={() => setActiveTab('constructor')}
        >
          <FileText size={16} /> {t('constructor:schedule.constructorTab')}
        </button>
        <button
          style={styles.tab(activeTab === 'schedules')}
          onClick={() => setActiveTab('schedules')}
        >
          <Clock size={16} /> {t('constructor:schedule.tabLabel')}
        </button>
        <button
          style={styles.tab(activeTab === 'consolidated')}
          onClick={() => setActiveTab('consolidated')}
        >
          <BarChart2 size={16} /> {t('constructor:consolidated.tabLabel')}
        </button>
      </div>

      {activeTab === 'constructor' && (
        <>
          {/* Индикатор шагов */}
          <div style={styles.stepsBar}>
            {STEPS.map((s, i) => {
              const active = s.number === step;
              const completed = s.number < step;
              return (
                <React.Fragment key={s.number}>
                  <div style={styles.stepItem(active, completed)}>
                    <div style={styles.stepCircle(active, completed)}>
                      {completed ? <CheckCircle size={18} /> : s.number}
                    </div>
                    <span style={styles.stepLabel(active)}>{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <div style={styles.stepConnector} />}
                </React.Fragment>
              );
            })}
          </div>

          {/* Контент шага */}
          <div style={styles.card}>
            {stepRenderers[step]()}

            {/* Навигация */}
            <div style={styles.divider} />
            <div style={styles.navRow}>
              <button
                style={styles.navBtn(step === 1)}
                onClick={goBack}
                disabled={step === 1}
              >
                <ChevronLeft size={18} />
                {t('constructor:nav.back')}
              </button>

              {step < 5 && (
                <button
                  style={styles.navBtn(!canGoNext())}
                  onClick={goNext}
                  disabled={!canGoNext()}
                >
                  {t('constructor:nav.next')}
                  <ChevronRight size={18} />
                </button>
              )}
            </div>
          </div>

          {/* История */}
          {renderHistory()}
        </>
      )}

      {activeTab === 'schedules' && renderSchedulesTab()}

      {activeTab === 'consolidated' && renderConsolidatedTab()}

      {/* Модал расписания */}
      {renderScheduleModal()}
    </div>
  );
}

export default InformationConstructor;
